// src/utils/pasteExpand.js

import dayjs from "lib/dayjs";

/* ---------------------------------------------
 * Clipboard parsing
 * --------------------------------------------- */

export function parseClipboardTable(raw) {
  const text = String(raw || "").replace(/\r/g, "");
  const lines = text
    .split("\n")
    .map((ln) => ln.trimEnd())
    .filter((ln) => ln.length > 0);

  if (!lines.length) return null;

  const grid = lines.map((ln) => ln.split("\t"));
  const rows = grid.length;
  const cols = Math.max(...grid.map((r) => r.length));

  return { grid, rows, cols };
}

/* ---------------------------------------------
 * Type coercers
 * --------------------------------------------- */

export function coerceNumberCell(x) {
  if (x == null) return null;
  const s = String(x).trim();
  if (!s) return null;

  const cleaned = s
    .replace(/\(/g, "-")
    .replace(/\)/g, "")
    .replace(/[^0-9.+-]/g, "");

  if (!cleaned) return null;

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function coerceDateCell(x) {
  if (x == null) return "";
  const s = String(x).trim();
  if (!s) return "";

  let d = dayjs(s);
  if (!d.isValid()) {
    const formats = [
      "YYYY-MM-DD",
      "M/D/YYYY",
      "MM/DD/YYYY",
      "M/D/YY",
      "MM/DD/YY",
      "MMM D YYYY",
      "MMM D, YYYY",
      "MMMM D YYYY",
      "MMMM D, YYYY",
    ];
    for (const f of formats) {
      const tryD = dayjs(s, f);
      if (tryD.isValid()) {
        d = tryD;
        break;
      }
    }
  }

  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DD");
}

export function coerceTextCell(x) {
  if (x == null) return "";
  return String(x);
}

function coerceByType(type, value) {
  if (type === "date") return coerceDateCell(value);
  if (type === "number") return coerceNumberCell(value);
  return coerceTextCell(value);
}

/* ---------------------------------------------
 * Grid → row objects via schema
 * --------------------------------------------- */

/**
 * Schema:
 * {
 *   columns: [{ key: "drawDate", type: "date" }, { key: "drawRequirement", type: "number" }],
 *   accept: ["Nx2", "2xN"],
 *   header: "auto-skip" | "none"
 * }
 */
export function clipboardToRowObjects(raw, schema) {
  const parsed = parseClipboardTable(raw);
  if (!parsed) return [];

  const { grid, rows, cols } = parsed;
  const columnDefs = Array.isArray(schema?.columns) ? schema.columns : [];
  if (columnDefs.length < 1) return [];

  const accept = Array.isArray(schema?.accept) ? schema.accept : ["Nx2"];
  const headerMode = schema?.header || "auto-skip";

  const neededCols = columnDefs.length;
  const out = [];

  const canTry2xN =
    accept.includes("2xN") &&
    rows === 2 &&
    cols >= neededCols &&
    neededCols === 2;

  if (canTry2xN) {
    for (let j = 0; j < cols; j++) {
      const obj = {};
      let ok = true;

      for (let k = 0; k < neededCols; k++) {
        const def = columnDefs[k];
        const cell = grid[k]?.[j];
        const coerced = coerceByType(def.type, cell);

        if (def.type === "date" && !coerced) ok = false;
        if (def.type === "number" && coerced == null) ok = false;

        obj[def.key] = coerced;
      }

      if (ok) out.push(obj);
    }

    if (out.length) return out;
  }

  const canTryNx2 = accept.includes("Nx2") && rows >= 1 && cols >= neededCols;
  if (!canTryNx2) return [];

  for (let i = 0; i < rows; i++) {
    const obj = {};
    let ok = true;

    for (let k = 0; k < neededCols; k++) {
      const def = columnDefs[k];
      const cell = grid[i]?.[k];
      const coerced = coerceByType(def.type, cell);

      if (def.type === "date" && !coerced) ok = false;
      if (def.type === "number" && coerced == null) ok = false;

      obj[def.key] = coerced;
    }

    if (i === 0 && headerMode === "auto-skip" && !ok) continue;
    if (ok) out.push(obj);
  }

  return out;
}

/* ---------------------------------------------
 * Merge/expand utility
 * --------------------------------------------- */

/**
 * startIndex modes:
 * - number: explicit index (0-based), overwrite starting there
 * - "append": always append
 * - "firstBlankOrAppend": first blank row, else append (old behavior)
 * - "firstBlankOrZero": first blank row, else overwrite from row 0 (new)
 */
export function applyPasteExpand({
  existingRows,
  newRows,
  makeRow,
  isBlankRow,
  startIndex = "firstBlankOrAppend",
}) {
  const cur = Array.isArray(existingRows) ? [...existingRows] : [];
  const incoming = Array.isArray(newRows) ? newRows : [];
  if (!incoming.length) return cur;

  const mk =
    typeof makeRow === "function"
      ? makeRow
      : (partial, i) => ({ ...partial, id: Date.now() + i });

  const isBlank =
    typeof isBlankRow === "function" ? isBlankRow : () => false;

  if (cur.length === 0) {
    cur.push(mk({}, 0));
  }

  let start;

  if (typeof startIndex === "number" && Number.isFinite(startIndex)) {
    start = Math.max(0, Math.min(startIndex, cur.length));
  } else if (startIndex === "append") {
    start = cur.length;
  } else {
    const idx = cur.findIndex((r) => isBlank(r));

    if (startIndex === "firstBlankOrZero") {
      start = idx >= 0 ? idx : 0;
    } else {
      // "firstBlankOrAppend" (default legacy behavior)
      start = idx >= 0 ? idx : cur.length;
    }
  }

  for (let i = 0; i < incoming.length; i++) {
    const idx = start + i;
    const partial = incoming[i];

    if (idx < cur.length) {
      cur[idx] = {
        ...cur[idx],
        ...partial,
        id: cur[idx]?.id ?? mk(partial, i)?.id ?? Date.now() + i,
      };
    } else {
      cur.push(mk(partial, i));
    }
  }

  return cur;
}
